import type {
  MarketStructureInput,
  MacdInput,
  MaInput,
  MasterPatternInput,
  LiquidationInput,
  OrderBlocksInput,
  RsiInput,
  VwapInput,
  ZigZagInput
} from '@server/data-feed/schemas/input.schema';
import {
  IndicatorType,
  LiquidationIndicatorMode,
  VwapMode,
  VwapSessionReset
} from '@shared/types';
import { TradingIndicatorConfig } from '@client/widgets/trading-form/trading-form.types';

const maDefaults: MaInput = {
  type: 'MA',
  period: 200,
  maType: 'SMA'
};

const rsiDefaults: RsiInput = {
  type: 'RSI',
  period: 20,
  ma: {
    type: 'MA',
    period: 30,
    maType: 'SMA'
  },
  zigZag: {
    type: 'ZIG_ZAG',
    deviationThreshold: 10,
    depth: 3
  }
};

const macdDefaults: MacdInput = {
  type: 'MACD',
  fastPeriod: 12,
  slowPeriod: 26,
  signalPeriod: 9
};

const vwapDefaults: VwapInput = {
  type: 'VWAP',
  mode: VwapMode.SESSION,
  sessionReset: VwapSessionReset.UTC_DAY
};

const orderBlocksDefaults: OrderBlocksInput = {
  type: 'ORDER_BLOCKS',
  gapCandleSizeRatio: 2.5,
  cancelOnMitigation: false
};

const zigZagDefaults: ZigZagInput = {
  type: 'ZIG_ZAG',
  deviationThreshold: 5,
  depth: 3
};

const masterPatternDefaults: MasterPatternInput = {
  type: 'MASTER_PATTERN',
  zigZag: {
    type: 'ZIG_ZAG',
    deviationThreshold: 1.5,
    depth: 3
  },
  allow_repainting: false,
  show_all_value_lines: false,
  value_expiration_diff: 0.5
};

const marketStructureDefaults: MarketStructureInput = {
  type: 'MARKET_STRUCTURE',
  zigZag: {
    type: 'ZIG_ZAG',
    deviationThreshold: 3,
    depth: 3
  }
};

type LiquidationIndicatorConfig = Extract<
  TradingIndicatorConfig,
  { type: 'LIQUIDATION' }
>;

const liquidationDefaults: LiquidationInput & {
  visualization?: LiquidationIndicatorConfig['visualization'];
} = {
  type: 'LIQUIDATION',
  mode: LiquidationIndicatorMode.LIQUIDATION,
  zigZag: {
    type: 'ZIG_ZAG',
    deviationThreshold: 1,
    depth: 3
  },
  pivotLookback: 3,
  reclaimWithinCandles: 10,
  confirmationCandles: 5,
  confirmationInvalidationMode: 'CLOSE',
  minBreakPercent: 0,
  minReactionPercent: 0,
  visualization: {
    showLines: true,
    showZigZag: false
  }
};

export const indicatorDefaults = {
  [IndicatorType.MA]: maDefaults,
  [IndicatorType.VWAP]: vwapDefaults,
  [IndicatorType.RSI]: rsiDefaults,
  [IndicatorType.MACD]: macdDefaults,
  [IndicatorType.ORDER_BLOCKS]: orderBlocksDefaults,
  [IndicatorType.ZIG_ZAG]: zigZagDefaults,
  [IndicatorType.MASTER_PATTERN]: masterPatternDefaults,
  [IndicatorType.MARKET_STRUCTURE]: marketStructureDefaults,
  [IndicatorType.LIQUIDATION]: liquidationDefaults
} as const;
